'use client';

/**
 * useRouteEditorLogic - Single source of truth for route editing
 *
 * All route creation/editing logic lives here. Components (RouteEditor,
 * RoutePanel) only call this hook and render.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';
import {
  useGetRouteDetailsQuery,
  useGetRouteStopsQuery,
  useCreateRouteMutation,
  useCreateSetRouteStopsMutation,
  useUpdateRouteMutation,
  useDeleteRouteMutation,
} from 'src/api/redux';
import { MAX_ROUTE_STOPS } from 'src/api/redux/routes/constants';
import { captureException } from 'src/observability';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import {
  endSession,
  selectRouteNavigationHydrated,
  selectRouteRoundTrip,
} from 'src/redux/slices/routeNavigationSlice';
import {
  setRoutesTempLabel,
  setRoutesTempStops,
  addRoutesTempStop,
  setIsLoadingDirections,
  setRoutesDirections,
  setDirectionsError,
  clearDirections,
  selectPendingProvider,
  clearPendingProvider,
  reorderRoutesTempStops,
  cancelRouteEdit,
  selectRoutesMode,
  selectSelectedRouteId,
} from 'src/redux';
import useRouteState from './useRouteState';
import useRouteActions from './useRouteActions';
import useRouteDirections from './useRouteDirections';
import useRouteOptimize, {
  OPTIMIZE_FAILED_ERROR,
  OPTIMIZE_LOCATION_ERROR,
} from './useRouteOptimize';
import useUserLocation from '../geolocation/useUserLocation';
import {
  enrichRouteStops,
  toApiStopPayload,
  createUserLocationStop,
  fetchMapboxOptimizedOrder,
} from './utils';
import type { EnrichedRouteStop, RouteDirections } from './types';

export interface UseRouteEditorLogicReturn {
  // State
  mode: string;
  tempLabel: string;
  tempStops: EnrichedRouteStop[];
  directions: RouteDirections | null;
  isLoadingDirections: boolean;
  selectedRouteId: string | null;

  // Derived flags
  isCreating: boolean;
  isEditing: boolean;
  isViewing: boolean;
  canSave: boolean;
  isOpen: boolean;

  // Loading states
  isLoading: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  isOptimizing: boolean;

  // Optimize
  canOptimize: boolean;
  optimizeError: string | null;

  // Handlers
  handleLabelChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleReorder: (fromIndex: number, toIndex: number) => void;
  handleRemove: (stopId: string) => void;
  handleSave: () => Promise<void>;
  handleDelete: () => Promise<void>;
  handleCancel: () => void;
  handleOptimize: () => Promise<void>;
}

/**
 * Reports an unexpected failure. API errors carry a `status` and are already
 * reported (500s) or expected (validation) at the API layer.
 */
const captureUnexpectedError = (error: unknown, context: string) => {
  if (typeof error === 'object' && error !== null && 'status' in error) return;
  captureException(error, { tags: { context } });
};

const useRouteEditorLogic = (): UseRouteEditorLogicReturn => {
  const dispatch = useAppDispatch();

  // Redux state
  const {
    mode,
    tempLabel,
    tempStops,
    directions,
    isLoadingDirections,
    isCreating,
    isEditing,
    isViewing,
    canSave,
    selectedRouteId,
  } = useRouteState();

  const { setLabel, removeStop, reorderStops, cancel } = useRouteActions();

  // RTK Query
  const { data: routeDetails, isLoading: isLoadingRouteDetails } =
    useGetRouteDetailsQuery(selectedRouteId || '', {
      skip: !selectedRouteId,
    });
  const { data: routeStops, isLoading: isLoadingRouteStops } =
    useGetRouteStopsQuery(selectedRouteId || '', {
      skip: !selectedRouteId,
    });

  // Mutations
  const [createRoute, { isLoading: isCreatingRoute }] =
    useCreateRouteMutation();
  const [setRouteStops, { isLoading: isSettingStops }] =
    useCreateSetRouteStopsMutation();
  const [updateRoute, { isLoading: isUpdatingRoute }] =
    useUpdateRouteMutation();
  const [deleteRouteMutation, { isLoading: isDeleting }] =
    useDeleteRouteMutation();
  const {
    optimize,
    error: optimizeError,
    setError: setOptimizeError,
  } = useRouteOptimize();
  // Covers the whole save -> location -> optimize operation, not only the request
  const [isOptimizingRoute, setIsOptimizingRoute] = useState(false);
  const isRouteNavigationHydrated = useAppSelector(
    selectRouteNavigationHydrated
  );
  const roundTrip = useAppSelector((state) =>
    selectRouteRoundTrip(state, Number(selectedRouteId))
  );

  // Directions
  const { fetchDirections: baseFetchDirections, isReady: directionsReady } =
    useRouteDirections();

  // User location
  const { location: userLocation, requestLocation } = useUserLocation();

  // Pending provider
  const pendingProvider = useSelector(selectPendingProvider);

  // Refs for insertion guards
  const prevStopsRef = useRef<EnrichedRouteStop[]>([]);
  const locationInsertingRef = useRef(false);
  const pendingProviderProcessedRef = useRef(false);

  // Derived states
  const isOpen = isCreating || isEditing || isViewing;
  const isLoading = isLoadingRouteDetails || isLoadingRouteStops;
  const isSaving = isCreatingRoute || isSettingStops || isUpdatingRoute;

  // ---------------------------------------------------------------------------
  // Hydration: load route data into temp state when editing/viewing
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if ((isEditing || isViewing) && routeDetails && tempLabel === '') {
      dispatch(setRoutesTempLabel(routeDetails.label));
    }
  }, [isEditing, isViewing, routeDetails, tempLabel, dispatch]);

  useEffect(() => {
    if ((isEditing || isViewing) && routeStops && tempStops.length === 0) {
      const enrichedStops = enrichRouteStops(routeStops);
      dispatch(setRoutesTempStops(enrichedStops));
    }
  }, [isEditing, isViewing, routeStops, tempStops.length, dispatch]);

  // ---------------------------------------------------------------------------
  // User location: sync presence of user location stop with GPS state
  //
  // Only for new routes, whose Mapbox optimize needs an origin stop. Saved
  // routes optimize on the backend from the user's location instead.
  // Inserts at position 0 when location is available, removes when cleared.
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (mode !== 'creating') return;

    const hasUserLocationStop = tempStops.some((s) => s.isUserLocation);

    if (userLocation && !hasUserLocationStop && !locationInsertingRef.current) {
      // Location available but no stop — insert at position 0
      locationInsertingRef.current = true;
      const locationStop = createUserLocationStop(userLocation);

      if (tempStops.length === 0) {
        dispatch(addRoutesTempStop(locationStop));
      } else {
        const updatedStops = [
          locationStop,
          ...tempStops.map((s) => ({ ...s, position: s.position + 1 })),
        ];
        dispatch(reorderRoutesTempStops(updatedStops));
      }
    } else if (hasUserLocationStop) {
      // Stop exists — insertion complete, clear the guard
      locationInsertingRef.current = false;
    }

    if (!userLocation && hasUserLocationStop) {
      // Location cleared but stop exists — remove it
      const locationStop = tempStops.find((s) => s.isUserLocation);
      if (locationStop) {
        removeStop(locationStop.id);
      }
    }
  }, [
    userLocation,
    mode,
    tempStops,
    dispatch,
    removeStop,
  ]);

  // ---------------------------------------------------------------------------
  // Pending provider: auto-add when entering create/edit mode
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!pendingProvider || pendingProviderProcessedRef.current) return;

    const shouldAdd =
      (isCreating && mode === 'creating') ||
      (isEditing && routeStops && !isLoadingRouteStops);

    if (shouldAdd) {
      const alreadyExists = tempStops.some(
        (stop) => stop.providerId === pendingProvider.id
      );

      if (!alreadyExists) {
        const newStop: EnrichedRouteStop = {
          id: `pending-${pendingProvider.id}`,
          providerId: pendingProvider.id,
          name: pendingProvider.name,
          address: pendingProvider.address,
          lat: pendingProvider.lat,
          lng: pendingProvider.lng,
          position: tempStops.length,
        };
        dispatch(addRoutesTempStop(newStop));
      }

      pendingProviderProcessedRef.current = true;
      dispatch(clearPendingProvider());
    }
  }, [
    pendingProvider,
    isCreating,
    isEditing,
    mode,
    routeStops,
    isLoadingRouteStops,
    tempStops,
    dispatch,
  ]);

  useEffect(() => {
    if (mode === 'idle') {
      pendingProviderProcessedRef.current = false;
    }
  }, [mode]);

  // ---------------------------------------------------------------------------
  // Auto-fetch directions when stops change (debounced)
  // ---------------------------------------------------------------------------

  const debouncedFetchDirections = useDebouncedCallback(
    async (stopsToFetch: EnrichedRouteStop[]) => {
      if (stopsToFetch.length < 2 || !directionsReady) {
        dispatch(clearDirections());
        return;
      }

      dispatch(setIsLoadingDirections(true));

      try {
        const result = await baseFetchDirections(stopsToFetch);

        if (result) {
          dispatch(setRoutesDirections(result));
        } else {
          dispatch(setDirectionsError('Failed to fetch directions'));
        }
      } catch (error) {
        dispatch(
          setDirectionsError(
            error instanceof Error ? error.message : 'Unknown error'
          )
        );
      } finally {
        dispatch(setIsLoadingDirections(false));
      }
    },
    500
  );

  useEffect(() => {
    if (mode !== 'creating' && mode !== 'editing') return;

    const fingerprint = tempStops
      .map((s) => `${s.id}-${s.position}-${s.lat}-${s.lng}-${s.addressKey || ''}`)
      .join(',');
    const prevFingerprint = prevStopsRef.current
      .map((s) => `${s.id}-${s.position}-${s.lat}-${s.lng}-${s.addressKey || ''}`)
      .join(',');

    if (fingerprint !== prevFingerprint) {
      prevStopsRef.current = tempStops;
      debouncedFetchDirections(tempStops);
    }

    return () => {
      debouncedFetchDirections.cancel();
    };
  }, [tempStops, mode, debouncedFetchDirections]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleLabelChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setLabel(e.target.value);
    },
    [setLabel]
  );

  const handleReorder = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (isOptimizingRoute) return;

      const newStops = [...tempStops];
      const [movedStop] = newStops.splice(fromIndex, 1);
      newStops.splice(toIndex, 0, movedStop);

      const stopsWithUpdatedPositions = newStops.map((stop, index) => ({
        ...stop,
        position: index,
      }));

      reorderStops(stopsWithUpdatedPositions);
    },
    [tempStops, reorderStops, isOptimizingRoute]
  );

  const handleRemove = useCallback(
    (stopId: string) => {
      if (isOptimizingRoute) return;
      removeStop(stopId);
    },
    [removeStop, isOptimizingRoute]
  );

  const handleSave = useCallback(async () => {
    if (!canSave || isOptimizingRoute) return;

    try {
      // Filter out user location stops — they are not persisted
      const stopsForApi = toApiStopPayload(tempStops);

      if (isCreating) {
        const newRoute = await createRoute({ label: tempLabel }).unwrap();
        await setRouteStops({
          routeId: newRoute.id,
          stops: stopsForApi,
        }).unwrap();
      } else if (isEditing && selectedRouteId) {
        const label = tempLabel.trim();
        if (!routeDetails || label !== routeDetails.label) {
          await updateRoute({ routeId: selectedRouteId, label }).unwrap();
        }
        await setRouteStops({
          routeId: selectedRouteId,
          stops: stopsForApi,
        }).unwrap();
      }

      dispatch(cancelRouteEdit());
    } catch (error) {
      captureUnexpectedError(error, 'route-save');
    }
  }, [
    canSave,
    isOptimizingRoute,
    isCreating,
    isEditing,
    selectedRouteId,
    routeDetails,
    tempLabel,
    tempStops,
    createRoute,
    updateRoute,
    setRouteStops,
    dispatch,
  ]);

  const handleDelete = useCallback(async () => {
    if (!selectedRouteId || isOptimizingRoute) return;
    try {
      await deleteRouteMutation(selectedRouteId).unwrap();
      dispatch(endSession(Number(selectedRouteId)));
      cancel();
    } catch (error) {
      captureUnexpectedError(error, 'route-delete');
    }
  }, [deleteRouteMutation, selectedRouteId, cancel, isOptimizingRoute, dispatch]);

  const handleCancel = useCallback(() => {
    if (isOptimizingRoute) return;
    cancel();
  }, [cancel, isOptimizingRoute]);

  const realStopCount = tempStops.filter((s) => !s.isUserLocation).length;
  // A saved route reads its round-trip choice from settings, so wait for them
  const canOptimize = isEditing
    ? isRouteNavigationHydrated &&
      realStopCount >= 2 &&
      realStopCount <= MAX_ROUTE_STOPS
    : tempStops.length >= 3;

  const handleOptimize = useCallback(async () => {
    if (!canOptimize || isOptimizingRoute) return;

    // Saved route: persist the current order, then let the backend reorder it
    // from the user's location (and store that origin on the route).
    if (isEditing && selectedRouteId) {
      setOptimizeError(null);
      setIsOptimizingRoute(true);
      try {
        // Ask for the location before saving: if it is denied, nothing has
        // been persisted yet and Cancel still discards the draft.
        const origin = userLocation ?? (await requestLocation());
        if (!origin) {
          setOptimizeError(OPTIMIZE_LOCATION_ERROR);
          return;
        }
        try {
          await setRouteStops({
            routeId: selectedRouteId,
            stops: toApiStopPayload(tempStops),
          }).unwrap();
        } catch (error) {
          captureUnexpectedError(error, 'route-optimize-save');
          setOptimizeError(OPTIMIZE_FAILED_ERROR);
          return;
        }
        const optimized = await optimize({
          routeId: selectedRouteId,
          roundtrip: roundTrip,
          origin: { lat: origin.lat, lng: origin.lng },
        });
        if (optimized) {
          // Read the store at apply time: the user may have moved to another
          // route (or closed the editor) while the request was in flight.
          dispatch((thunkDispatch, getState) => {
            const state = getState();
            if (
              selectRoutesMode(state) === 'editing' &&
              selectSelectedRouteId(state) === selectedRouteId
            ) {
              thunkDispatch(setRoutesTempStops(enrichRouteStops(optimized)));
            }
          });
        }
      } finally {
        setIsOptimizingRoute(false);
      }
      return;
    }

    dispatch(setIsLoadingDirections(true));

    try {
      const optimizedStops = await fetchMapboxOptimizedOrder(tempStops);

      if (optimizedStops) {
        reorderStops(optimizedStops);
      } else {
        dispatch(setDirectionsError('Failed to optimize route'));
      }
    } catch (error) {
      dispatch(
        setDirectionsError(
          error instanceof Error ? error.message : 'Unknown error'
        )
      );
    } finally {
      dispatch(setIsLoadingDirections(false));
    }
  }, [
    canOptimize,
    isOptimizingRoute,
    setOptimizeError,
    isEditing,
    selectedRouteId,
    roundTrip,
    tempStops,
    setRouteStops,
    userLocation,
    requestLocation,
    optimize,
    reorderStops,
    dispatch,
  ]);

  return {
    mode,
    tempLabel,
    tempStops,
    directions,
    isLoadingDirections,
    selectedRouteId,
    isCreating,
    isEditing,
    isViewing,
    canSave,
    isOpen,
    isLoading,
    isSaving,
    isDeleting,
    isOptimizing: isOptimizingRoute,
    canOptimize,
    optimizeError,
    handleLabelChange,
    handleReorder,
    handleRemove,
    handleSave,
    handleDelete,
    handleCancel,
    handleOptimize,
  };
};

export default useRouteEditorLogic;
